import React, { useState, useEffect, FC } from 'react';

import { useGetUserApplicationsQuery } from 'reduxState/store/application/api';
import { Application, AppGroup } from 'reduxState/store/application/types';
import CampaignsAppList from './CampaignsAppList/CampaignsAppList';
import { ICampaign } from 'reduxState/store/campaigns/types';

export type selectedAppsType = Map<Application['id'], Application>;

/**
 * CampaignAppSelector component for selecting applications to include in a campaign.
 *
 * This component provides an interface for users to select which applications
 * should be associated with a campaign. It displays available applications that
 * can be added and currently selected applications that can be removed.
 *
 * @component
 * @param {Object} props - The component props
 * @param {(apps: Application[]) => void} props.setSelectedApps - Callback function to update the selected applications array
 * @param {boolean} props.hasSelectedAppsError - Flag indicating if there's an error with app selection
 * @param {(hasError: boolean) => void} props.setHasSelectedAppsError - Callback to set the error state for app selection
 * @param {ICampaign | null} props.campaign - Campaign object to edit or duplicate
 * @param {boolean} [props.disabled=false] - Flag to disable the component
 * @param {boolean} [props.reset=false] - Flag to reset the component state
 *
 * @example
 * ```tsx
 * <CampaignAppSelector
 *   setSelectedApps={handleSetSelectedApps}
 *   hasSelectedAppsError={hasError}
 *   setHasSelectedAppsError={setHasError}
 *   campaign={campaignToEdit}
 *   disabled={isDisabled}
 *   reset={shouldReset}
 * />
 * ```
 */

const sortAppsByName = (apps: Application[]) => {
  return apps.sort((a, b) => a.name.localeCompare(b.name));
};

interface ICampaignAppsSelector {
  setSelectedApps: (apps: Application[]) => void;
  hasSelectedAppsError: boolean;
  setHasSelectedAppsError: (hasError: boolean) => void;
  campaign: ICampaign | null;
  appGroup?: AppGroup;
  disabled?: boolean;
  reset?: boolean;
}

const CampaignAppSelector: FC<ICampaignAppsSelector> = ({
  setSelectedApps,
  hasSelectedAppsError,
  setHasSelectedAppsError,
  campaign,
  appGroup = null,
  disabled = false,
  reset = false,
}) => {
  const { data: applicationsData } = useGetUserApplicationsQuery();
  const [addableApps, setAddableApps] = useState<Application[]>([]);
  const [removableApps, setRemovableApps] = useState<Application[]>([]);

  useEffect(() => {
    if (!applicationsData) return;

    if (!campaign) {
      const apps = appGroup ? appGroup.applications : applicationsData.applications;
      setAddableApps(sortAppsByName([...apps]));
      setRemovableApps([]);
      return;
    }

    const allApps = sortAppsByName([...applicationsData.applications]);
    const includedAppGroups: string[] = [];

    // Find selected apps and track their app groups
    const selectedApps = allApps.filter(app => {
      const isSelected = campaign.applications.some(campaignApp => campaignApp.id.toString() === app.id.toString());
      if (isSelected && app.adminId && !includedAppGroups.includes(app.adminId)) {
        includedAppGroups.push(app.adminId);
      }
      return isSelected;
    });

    setRemovableApps(selectedApps);
    setSelectedApps(selectedApps);

    // Set addable apps based on whether an app group is specified
    if (appGroup) {
      const newAddableApps = includedAppGroups
        .flatMap(groupId => applicationsData.applications.filter(app => app.adminId === groupId))
        .filter(app => !selectedApps.some(selectedApp => selectedApp.id.toString() === app.id.toString()));
      setAddableApps(sortAppsByName(newAddableApps));
    } else {
      setAddableApps(
        allApps.filter(app => !selectedApps.some(selectedApp => selectedApp.id.toString() === app.id.toString())),
      );
    }
  }, [applicationsData, campaign, setSelectedApps, appGroup]);

  useEffect(() => {
    if (reset && applicationsData) {
      if (appGroup) {
        const appGroupApps = appGroup.applications;
        setAddableApps(sortAppsByName([...appGroupApps]));
      } else {
        setAddableApps(sortAppsByName([...applicationsData.applications]));
      }
      setRemovableApps([]);
      setSelectedApps([]);
    }
  }, [reset, applicationsData, setSelectedApps, appGroup]);

  const handleAddApps = (apps: Application[]) => {
    const updatedRemovableApps = sortAppsByName([...removableApps, ...apps]);
    setRemovableApps(updatedRemovableApps);
    setAddableApps(prevAddableApps =>
      sortAppsByName(prevAddableApps.filter(app => !apps.some(addedApp => addedApp.id === app.id))),
    );

    setSelectedApps(updatedRemovableApps);

    if (hasSelectedAppsError) setHasSelectedAppsError(false);
  };

  const handleRemoveApps = (apps: Application[]) => {
    setAddableApps(prevAddableApps => sortAppsByName([...prevAddableApps, ...apps]));
    const updatedRemovableApps = removableApps.filter(app => !apps.some(removedApp => removedApp.id === app.id));
    const sortedUpdatedRemovableApps = sortAppsByName(updatedRemovableApps);

    setRemovableApps(sortedUpdatedRemovableApps);
    setSelectedApps(sortedUpdatedRemovableApps);
  };

  return (
    <div className={`flex flex-col p-2 min-w-0 max-w-full ${hasSelectedAppsError ? 'border border-error-red rounded-md' : ''}`}>
      {hasSelectedAppsError && <p className="text-center text-md text-error-red">Please select at least one app</p>}
      <div className="flex flex-col sm:flex-row gap-4 min-w-0">
        {!disabled && <CampaignsAppList apps={addableApps} setApps={handleAddApps} title="Addable" />}
        <CampaignsAppList apps={removableApps} setApps={handleRemoveApps} title="Selected" disabled={disabled} />
      </div>
    </div>
  );
};

export default CampaignAppSelector;
